import { useMemo } from "react";
import type { GatewayTierDefaultsGroup } from "@dosia/shared";
import { useApiQuery } from "../lib/useApiQuery.js";
import { platformGatewayTierDefaultsPath } from "../lib/api-paths.js";

/**
 * 「网关档位默认」只读面板 —— 维护者口径「网关默认唯一，DOSIA 全不配」的后台一半。
 *
 * 展示网关为每个分组派生的 opus / sonnet / haiku 三元组：Agent SDK 干隐藏小活（起标题、压缩
 * 上下文、探活）时会发写死的 `claude-*` id，这张表说的是「在这个组里，那三个 id 各自实际打到
 * 哪个模型」。deepseek / glm / kimi 这类走原生 anthropic 分发器的组没有这张表就会 404。
 *
 * **只读，没有编辑入口**——档位是网关按各组的价格与账号自动派生的，要改得去网关侧改那两样。
 * 这里放个可编辑表单只会造出第二个真相源。
 *
 * 数据来自公开清单（平台级超集）。员工端实际注入运行时的是 org bundle 里那份（按 org 真实账号
 * 派生；⚠️ 它点名的档位也不保证可路由，见 REQ-293），两者绝大多数时候一致；不一致的情形是「某个 org 没有这个模型的凭据」，
 * 此时员工端会自己跳过不可路由的档位。面板下方的说明把这点写给超管看。
 */
export function GatewayTierDefaultsPanel(): JSX.Element {
  const q = useApiQuery<{ groups: GatewayTierDefaultsGroup[]; source: "live" | "unavailable" }>(
    platformGatewayTierDefaultsPath(),
    [],
  );
  const groups = useMemo(() => q.data?.groups ?? [], [q.data]);
  const unavailable = !q.loading && (q.error != null || q.data?.source !== "live");

  return (
    <section className="rounded border border-dosia-border bg-dosia-bg-light">
      <header className="border-b border-dosia-border px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">网关档位默认（tier_defaults）</h2>
          {q.loading && <span className="text-xs text-dosia-text-secondary">加载中…</span>}
        </div>
        <p className="mt-1 text-xs text-dosia-text-secondary">
          Agent SDK 干隐藏小活（起标题、压缩上下文、探活）时会发写死的 opus / sonnet / haiku
          家族 id。下表是网关为每个分组派生的「这三个 id 各自实际打到哪个模型」。
          <span className="font-medium">只读</span>——档位由网关按各组的刊例价与账号自动派生，
          要调整请在网关侧改价格 / 账号策展，DOSIA 不再各自配置。
        </p>
      </header>

      {unavailable ? (
        <div className="px-4 py-6 text-center text-sm text-dosia-text-secondary">
          暂时读不到网关档位默认（网关不可达 / 清单未发布 / gatewayBaseUrl 未配置）。
          <div className="mt-1 text-xs">
            这里<span className="font-medium">不回落打包快照</span>——档位跟着网关的实时价格与账号走，
            拿陈旧快照当现状展示会让人以为线上此刻就是这样。
          </div>
        </div>
      ) : groups.length === 0 && !q.loading ? (
        <div className="px-4 py-6 text-center text-sm text-dosia-text-secondary">
          网关当前没有为任何分组下发档位默认。
        </div>
      ) : (
        <div className="divide-y divide-dosia-border">
          {groups.map((g) => (
            <div key={g.groupId} className="px-4 py-3">
              <div className="mb-2 flex items-baseline gap-2">
                <span className="text-sm font-medium">{g.groupName}</span>
                <span className="font-mono text-xs text-dosia-text-dim">#{g.groupId}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[36rem] text-left text-xs">
                  <thead className="text-[11px] uppercase tracking-wide text-dosia-text-dim">
                    <tr>
                      <th className="py-1 pr-3 font-semibold">主模型</th>
                      <th className="py-1 pr-3 font-semibold">opus 档</th>
                      <th className="py-1 pr-3 font-semibold">sonnet 档</th>
                      <th className="py-1 font-semibold">haiku 档</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-dosia-text-secondary">
                    {g.rows.map((r) => (
                      <tr key={r.modelId} className="border-t border-dosia-border/60">
                        <td className="py-1.5 pr-3 text-dosia-text">{r.modelId}</td>
                        <td className="py-1.5 pr-3">{r.opus ?? "—"}</td>
                        <td className="py-1.5 pr-3">{r.sonnet ?? "—"}</td>
                        <td className="py-1.5">{r.haiku ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      <footer className="border-t border-dosia-border px-4 py-2 text-[11px] leading-relaxed text-dosia-text-dim">
        逐行不同是正常的：网关按「主脑即上限」特化——选了 sonnet 的人，opus 档也是 sonnet，
        不会被静默升级到更贵的模型。本表读的是<span className="font-medium">公开清单</span>
        （平台级超集）；员工端实际注入的是各 org bundle 里按真实账号派生的那一份，
        某个 org 缺对应凭据时该档位在员工端会被跳过。
      </footer>
    </section>
  );
}
